"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef } from "react";
import { toast } from "sonner";
import { sourced, unavailable, type ReviewValue } from "@/components/ui/transaction-review-contract";
import { confirmAcceptOfferStep, confirmOfferStep } from "@/features/finance/api";
import { classifyChainPost, type ChainWorkMode } from "@/features/finance/chain-settlement";
import { trackChainWork } from "@/features/finance/chain-work-dock";
import { useExternalStepHandoff } from "@/features/finance/hooks/useExternalStepHandoff";
import { submitOfferDecision } from "@/features/finance/offer-decision";
import { refreshQuietly } from "@/features/finance/place-offer";
import type { OfferItem } from "@/features/finance/types";
import { divideDecimalString, formatDecimal, parseApiAmount } from "@/lib/decimal-amount";
import { formatDecimalString } from "@/lib/token-amount";
import type { TransactionRecord } from "@/lib/transaction-flow";
import { buildReviewRows } from "../review-rows";
import { formatUnixDate, type FlowModel, type SharedFlowProps } from "./shared";
export type OfferDecisionProps = SharedFlowProps & {
  mode: "accept" | "cancel";
  offer: OfferItem;
  fallbackSymbol: string;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
};

/**
 * ACCEPT OR CANCEL AN OFFER — the legacy `OfferDecisionFlow`, moved verbatim:
 * one dialog, the mode deciding the words and the endpoint; the question
 * asked about the exact figure the request acts on; the external wallet's
 * rounds against `offer.id` (accept may go round twice); the decision in
 * `submitOfferDecision`; a queued accept never says "accepted" — ownership has
 * not moved yet, the dock says when it does.
 */
export function useOfferDecision(p) {
  const $ = _c(51);
  const {
    lang,
    assetName,
    assetImage,
    collectionName,
    tokenId,
    networkLabel,
    royaltyPercent,
    contractAddress,
    onRecord,
    mode,
    offer,
    fallbackSymbol,
    onClose,
    onSuccess
  } = p;
  const isFa = lang === "fa";
  const isAccept = mode === "accept";
  const handoff = useExternalStepHandoff(lang, "offer_id");
  const handedToDock = useRef(false);
  const chainMode = isAccept ? "acceptOffer" : "cancelOffer";
  const quantity = Number.isInteger(offer.quantity) && offer.quantity > 0 ? offer.quantity : 1;
  let confirmQuestion;
  let symbol;
  let t0;
  let total;
  if ($[0] !== assetName || $[1] !== collectionName || $[2] !== fallbackSymbol || $[3] !== isAccept || $[4] !== isFa || $[5] !== lang || $[6] !== networkLabel || $[7] !== offer.currency?.symbol || $[8] !== offer.deadline || $[9] !== offer.total_price || $[10] !== quantity || $[11] !== royaltyPercent || $[12] !== tokenId) {
    const parsedTotal = parseApiAmount(offer.total_price);
    total = parsedTotal ? formatDecimal(parsedTotal) : null;
    const perItem = total ? divideDecimalString(total, quantity, 18) : null;
    symbol = offer.currency?.symbol?.toUpperCase() ?? null;
    const expiryText = formatUnixDate(offer.deadline, lang);
    const amountText = total ? `${formatDecimalString(total, {
      locale: lang,
      maxFractionDigits: 18
    })}${symbol ? ` ${symbol}` : ""}` : null;
    confirmQuestion = isAccept ? amountText ? isFa ? `آیا از قبول پیشنهاد ${amountText} مطمئن هستید؟` : `Are you sure you want to accept the ${amountText} offer?` : isFa ? "\u0622\u06CC\u0627 \u0627\u0632 \u0642\u0628\u0648\u0644 \u0627\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0645\u0637\u0645\u0626\u0646 \u0647\u0633\u062A\u06CC\u062F\u061F" : "Are you sure you want to accept this offer?" : isFa ? "\u0622\u06CC\u0627 \u0627\u0632 \u0644\u063A\u0648 \u0627\u06CC\u0646 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0645\u0637\u0645\u0626\u0646 \u0647\u0633\u062A\u06CC\u062F\u061F" : "Are you sure you want to cancel this offer?";
    const conditions = isAccept ? sourced(isFa ? "\u067E\u0630\u06CC\u0631\u0634 \u0622\u0641\u0631 \u0645\u0627\u0644\u06A9\u06CC\u062A \u0631\u0627 \u0645\u0646\u062A\u0642\u0644 \u0645\u06CC\u200C\u06A9\u0646\u062F \u0648 \u0644\u06CC\u0633\u062A \u0641\u0631\u0648\u0634 \u0641\u0639\u0627\u0644 \u0627\u06CC\u0646 \u0622\u06CC\u062A\u0645 \u0631\u0627 \u0645\u06CC\u200C\u0628\u0646\u062F\u062F." : "Accepting transfers ownership and closes any active listing on this item.") : sourced(isFa ? "\u0644\u063A\u0648\u060C \u0627\u06CC\u0646 \u0622\u0641\u0631 \u0631\u0627 \u067E\u0633 \u0645\u06CC\u200C\u06AF\u06CC\u0631\u062F. \u0622\u0641\u0631\u0647\u0627\u06CC \u062F\u06CC\u06AF\u0631 \u0648 \u0644\u06CC\u0633\u062A \u0641\u0631\u0648\u0634 \u062A\u063A\u06CC\u06CC\u0631\u06CC \u0646\u0645\u06CC\u200C\u06A9\u0646\u0646\u062F." : "Cancelling withdraws this offer. Other offers and any listing are unaffected.");
    t0 = buildReviewRows({
      lang,
      perspective: isAccept ? "seller" : "buyer",
      assetName,
      collectionName,
      tokenId,
      quantity,
      unitPrice: perItem?.text ?? null,
      unitPriceRounded: perItem ? !perItem.exact : false,
      total,
      currencySymbol: symbol,
      currencyUnavailableReason: isFa ? `این آفر ارز خود را اعلام نکرده است. ${fallbackSymbol} برچسب شبکه است، نه نام ارز.` : `This offer does not state its currency. ${fallbackSymbol} is the network label, not a currency name.`,
      networkLabel,
      balance: null,
      balanceUnavailableReason: isAccept ? isFa ? "\u067E\u0630\u06CC\u0631\u0634 \u0622\u0641\u0631 \u0627\u0632 \u0645\u0648\u062C\u0648\u062F\u06CC \u0634\u0645\u0627 \u0628\u0631\u062F\u0627\u0634\u062A \u0646\u0645\u06CC\u200C\u06A9\u0646\u062F." : "Accepting an offer does not draw on your balance." : isFa ? "\u0644\u063A\u0648 \u0622\u0641\u0631 \u0627\u0632 \u0645\u0648\u062C\u0648\u062F\u06CC \u0634\u0645\u0627 \u0628\u0631\u062F\u0627\u0634\u062A \u0646\u0645\u06CC\u200C\u06A9\u0646\u062F." : "Cancelling an offer does not draw on your balance.",
      royaltyPercent,
      expiration: expiryText ? sourced(expiryText) : unavailable(isFa ? "\u0627\u06CC\u0646 \u0622\u0641\u0631 \u062A\u0627\u0631\u06CC\u062E \u0627\u0646\u0642\u0636\u0627\u06CC \u0645\u0639\u062A\u0628\u0631\u06CC \u0646\u062F\u0627\u0631\u062F." : "This offer carries no usable deadline."),
      conditions
    });
    $[0] = assetName;
    $[1] = collectionName;
    $[2] = fallbackSymbol;
    $[3] = isAccept;
    $[4] = isFa;
    $[5] = lang;
    $[6] = networkLabel;
    $[7] = offer.currency?.symbol;
    $[8] = offer.deadline;
    $[9] = offer.total_price;
    $[10] = quantity;
    $[11] = royaltyPercent;
    $[12] = tokenId;
    $[13] = confirmQuestion;
    $[14] = symbol;
    $[15] = t0;
    $[16] = total;
  } else {
    confirmQuestion = $[13];
    symbol = $[14];
    t0 = $[15];
    total = $[16];
  }
  const rows = t0;
  let t1;
  if ($[17] !== assetImage || $[18] !== assetName || $[19] !== chainMode || $[20] !== contractAddress || $[21] !== handoff || $[22] !== isAccept || $[23] !== isFa || $[24] !== lang || $[25] !== mode || $[26] !== offer.id || $[27] !== onSuccess || $[28] !== symbol || $[29] !== tokenId || $[30] !== total) {
    t1 = async requestId => {
      const flow = isAccept ? "accept-offer" : "cancel-offer";
      const offerRef = [{
        label: isFa ? "\u0634\u0646\u0627\u0633\u0647 \u0622\u0641\u0631" : "Offer ID",
        value: String(offer.id),
        technical: true
      }];
      const success = message => {
        toast.success(message);
        return {
          requestId,
          flow,
          outcome: "success",
          title: isAccept ? isFa ? "\u0622\u0641\u0631 \u067E\u0630\u06CC\u0631\u0641\u062A\u0647 \u0634\u062F" : "Offer accepted" : isFa ? "\u0622\u0641\u0631 \u0644\u063A\u0648 \u0634\u062F" : "Offer cancelled",
          description: total ? `${formatDecimalString(total, {
            locale: lang,
            maxFractionDigits: 18
          })}${symbol ? ` ${symbol}` : ""}` : undefined,
          refs: offerRef,
          at: new Date().toISOString()
        };
      };
      const submitted = () => {
        const text = isAccept ? isFa ? "\u067E\u0630\u06CC\u0631\u0634 \u062B\u0628\u062A \u0634\u062F\u061B \u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u0627\u0646\u062A\u0642\u0627\u0644 \u0631\u0648\u06CC \u0628\u0644\u0627\u06A9\u0686\u06CC\u0646" : "Acceptance submitted \u2014 waiting for the on-chain transfer" : isFa ? "\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u0644\u063A\u0648 \u062B\u0628\u062A \u0634\u062F\u061B \u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u0628\u0644\u0627\u06A9\u0686\u06CC\u0646" : "Cancellation submitted \u2014 waiting for the blockchain";
        toast.success(text);
        return {
          requestId,
          flow,
          outcome: "success",
          title: text,
          description: isFa ? "\u0646\u062A\u06CC\u062C\u0647 \u062F\u0631 \u06A9\u0627\u062F\u0631 \u06AF\u0648\u0634\u0647\u200C\u06CC \u0635\u0641\u062D\u0647 \u062F\u0646\u0628\u0627\u0644 \u0645\u06CC\u200C\u0634\u0648\u062F \u0648 \u0647\u0645\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0628\u0647\u200C\u0645\u062D\u0636 \u0646\u0647\u0627\u06CC\u06CC \u0634\u062F\u0646 \u0628\u0647\u200C\u0631\u0648\u0632 \u0645\u06CC\u200C\u0634\u0648\u062F." : "The card at the corner of the page follows it, and this page updates as soon as it settles.",
          refs: offerRef,
          at: new Date().toISOString()
        };
      };
      const failure = message_0 => ({
        requestId,
        flow,
        outcome: "failure",
        title: isAccept ? isFa ? "\u0622\u0641\u0631 \u067E\u0630\u06CC\u0631\u0641\u062A\u0647 \u0646\u0634\u062F" : "The offer was not accepted" : isFa ? "\u0622\u0641\u0631 \u0644\u063A\u0648 \u0646\u0634\u062F" : "The offer was not cancelled",
        description: message_0,
        refs: offerRef,
        at: new Date().toISOString()
      });
      const fallbackSuccessMessage = isAccept ? isFa ? "\u0622\u0641\u0631 \u067E\u0630\u06CC\u0631\u0641\u062A\u0647 \u0634\u062F" : "Offer accepted" : isFa ? "\u0622\u0641\u0631 \u0644\u063A\u0648 \u0634\u062F" : "Offer cancelled";
      if (handoff.pending) {
        const result = await handoff.sign((offerId, payload) => isAccept ? confirmAcceptOfferStep(contractAddress, tokenId, offerId, payload) : confirmOfferStep(contractAddress, tokenId, offerId, payload));
        if (!result) {
          return null;
        }
        if (result.outcome === "pending_confirmation") {
          return null;
        }
        await onSuccess();
        return success(result.lastResponse?.message || fallbackSuccessMessage);
      }
      const {
        outcome,
        message: message_1,
        response
      } = await submitOfferDecision({
        mode,
        contractAddress,
        tokenId,
        offerId: offer.id,
        onConfirmed: onSuccess,
        fallbackSuccessMessage,
        fallbackFailureMessage: isAccept ? isFa ? "\u0645\u0627\u0644\u06A9\u06CC\u062A \u0645\u0646\u062A\u0642\u0644 \u0646\u0634\u062F \u0648 \u0622\u0641\u0631 \u0647\u0645\u0686\u0646\u0627\u0646 \u0628\u0627\u0632 \u0627\u0633\u062A." : "Ownership did not transfer and the offer is still open." : isFa ? "\u0622\u0641\u0631 \u0634\u0645\u0627 \u0647\u0645\u0686\u0646\u0627\u0646 \u0628\u0627\u0632 \u0627\u0633\u062A." : "Your offer is still open."
      });
      if (outcome === "success") {
        if (classifyChainPost(response, chainMode) === "queued") {
          handedToDock.current = true;
          trackChainWork({
            mode: chainMode,
            title: assetName,
            image: assetImage,
            contractAddress,
            tokenId,
            lang,
            workId: offer.id
          });
          await refreshQuietly(onSuccess);
          return submitted();
        }
        return success(message_1);
      }
      if (outcome === "sign_required" && response) {
        const handed = handoff.receive(response, offer.id);
        if (handed.kind === "handoff") {
          return null;
        }
        if (handed.kind === "unusable") {
          return failure(isFa ? "\u0633\u0631\u0648\u0631 \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u06A9\u0627\u0631 \u0628\u0647 \u0627\u0645\u0636\u0627\u06CC \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0646\u06CC\u0627\u0632 \u062F\u0627\u0634\u062A \u0627\u0645\u0627 \u062A\u0631\u0627\u06A9\u0646\u0634\u06CC \u0646\u0641\u0631\u0633\u062A\u0627\u062F." : "The server required a wallet signature but sent no transaction.");
        }
        await onSuccess();
        return success(message_1 || fallbackSuccessMessage);
      }
      return failure(message_1);
    };
    $[17] = assetImage;
    $[18] = assetName;
    $[19] = chainMode;
    $[20] = contractAddress;
    $[21] = handoff;
    $[22] = isAccept;
    $[23] = isFa;
    $[24] = lang;
    $[25] = mode;
    $[26] = offer.id;
    $[27] = onSuccess;
    $[28] = symbol;
    $[29] = tokenId;
    $[30] = total;
    $[31] = t1;
  } else {
    t1 = $[31];
  }
  const onConfirm = t1;
  const t2 = isAccept ? isFa ? "\u067E\u0630\u06CC\u0631\u0634 \u0622\u0641\u0631" : "Accept offer" : isFa ? "\u0644\u063A\u0648 \u0622\u0641\u0631" : "Cancel offer";
  const t3 = isAccept ? isFa ? "\u067E\u0630\u06CC\u0631\u0634\u060C \u0645\u0627\u0644\u06A9\u06CC\u062A \u0627\u06CC\u0646 NFT \u0631\u0627 \u0645\u0646\u062A\u0642\u0644 \u0645\u06CC\u200C\u06A9\u0646\u062F \u0648 \u0642\u0627\u0628\u0644 \u0628\u0631\u06AF\u0634\u062A \u0646\u06CC\u0633\u062A." : "Accepting transfers ownership of this NFT and cannot be undone." : isFa ? "\u0622\u0641\u0631 \u0644\u063A\u0648\u200C\u0634\u062F\u0647 \u0628\u0627\u0632\u06AF\u0631\u062F\u0627\u0646\u062F\u0647 \u0646\u0645\u06CC\u200C\u0634\u0648\u062F\u061B \u0628\u0631\u0627\u06CC \u0627\u062F\u0627\u0645\u0647 \u0628\u0627\u06CC\u062F \u0622\u0641\u0631 \u062C\u062F\u06CC\u062F\u06CC \u062B\u0628\u062A \u06A9\u0646\u06CC\u062F." : "A cancelled offer cannot be restored \u2014 you would have to place a new one.";
  const t4 = handoff.continueLabel ?? (isAccept ? isFa ? "\u067E\u0630\u06CC\u0631\u0634 \u0622\u0641\u0631" : "Accept offer" : isFa ? "\u0644\u063A\u0648 \u0622\u0641\u0631" : "Cancel offer");
  const t5 = isAccept ? isFa ? "\u0645\u0627\u0644\u06A9\u06CC\u062A \u0645\u0646\u062A\u0642\u0644 \u0645\u06CC\u200C\u0634\u0648\u062F \u0648 \u0642\u0627\u0628\u0644 \u0628\u0631\u06AF\u0634\u062A \u0646\u06CC\u0633\u062A" : "ownership transfers and this cannot be undone" : isFa ? "\u0627\u06CC\u0646 \u0622\u0641\u0631 \u0628\u0627\u0632\u06AF\u0631\u062F\u0627\u0646\u062F\u0647 \u0646\u0645\u06CC\u200C\u0634\u0648\u062F" : "this offer cannot be restored";
  const t6 = handoff.pending ? isFa ? "\u062F\u0631 \u0627\u0646\u062A\u0638\u0627\u0631 \u06A9\u06CC\u0641 \u067E\u0648\u0644 \u0634\u0645\u0627..." : "Waiting for your wallet\u2026" : isAccept ? isFa ? "\u062F\u0631 \u062D\u0627\u0644 \u0642\u0628\u0648\u0644 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F..." : "Accepting the offer..." : isFa ? "\u062F\u0631 \u062D\u0627\u0644 \u0644\u063A\u0648 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F..." : "Cancelling the offer...";
  const t7 = isAccept ? "primary" : "danger";
  const t8 = handoff.blockedReason;
  let t9;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => handedToDock.current;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== handoff || $[34] !== onClose) {
    t10 = handoff.guardClose(onClose);
    $[33] = handoff;
    $[34] = onClose;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== confirmQuestion || $[37] !== handoff || $[38] !== lang || $[39] !== networkLabel || $[40] !== onConfirm || $[41] !== onRecord || $[42] !== rows || $[43] !== t10 || $[44] !== t2 || $[45] !== t3 || $[46] !== t4 || $[47] !== t5 || $[48] !== t6 || $[49] !== t7) {
    const model = {
      lang,
      title: t2,
      description: confirmQuestion,
      rows,
      irreversibility: t3,
      confirmLabel: t4,
      confirmConsequence: t5,
      confirmLoadingLabel: t6,
      confirmColor: t7,
      blockedReason: t8,
      hasInput: false,
      handoff,
      networkLabel,
      onConfirm,
      closeOnSuccess: t9,
      onRecord,
      onClose: t10
    };
    t11 = {
      model
    };
    $[36] = confirmQuestion;
    $[37] = handoff;
    $[38] = lang;
    $[39] = networkLabel;
    $[40] = onConfirm;
    $[41] = onRecord;
    $[42] = rows;
    $[43] = t10;
    $[44] = t2;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t6;
    $[49] = t7;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  return t11;
}